import { Routes } from '@angular/router';
import { dossierResolver } from '@shared/resolvers/dossier.resolver';
import { PageTitle } from '@shared/enums/page-title.enum';
import { DroitClientFinal } from '@logaia_users/logaia-dto';
import { dossierIsLibreGuard } from '@app/guards/dossier-is-libre.guard';
import { dossierAccessGuard } from '@app/guards/dossier-access.guard';
import { pendingChangesGuard } from '@app/guards/pending-changes.guard';
import { hasExerciceComptableGuard } from '@app/guards/exercice-comptable.guard';
import { inject } from '@angular/core';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { dossierDeTravailGuard } from '@app/guards/dossier-de-travail.guard';
import { collabGuard } from '@app/guards/collab.guard';
import { authGuard } from '@app/guards/auth.guard';
import { balanceHelpCodeResolver } from '@shared/resolvers/balance-help-code.resolver';
import { grandLivreHelpCodeResolver } from '@shared/resolvers/grand-livre-code.resolver';
import { centralisateurHelpCodeResolver } from '@shared/resolvers/centralisateur-help-code.resolver';
import { clientGuard } from '@app/guards/client.guard';
import { PreferenceService } from '@shared/services/cabinet/preference.service';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'signin',
    pathMatch: 'full',
  },
  {
    path: 'signin',
    title: 'Logaia',
    loadComponent: () =>
      import('./routes/signin/signin.component').then((c) => c.SigninComponent),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./routes/home.component').then((c) => c.HomeComponent),
    children: [
      {
        path: 'gestion-interne/factures',
        title: 'Logaia',
        canActivate: [collabGuard],
        loadComponent: () =>
          import('./routes/gestion-interne/factures/factures.component').then(
            (c) => c.FacturesComponent,
          ),
      },
      {
        path: 'dossiers',
        title: 'Logaia',
        canActivate: [collabGuard],
        data: { helpCodeEXC: '070' },
        loadComponent: () =>
          import('./routes/dossiers/dossiers.component').then(
            (c) => c.DossiersComponent,
          ),
      },
      {
        path: 'dossier/:id',
        canActivate: [dossierIsLibreGuard],
        canActivateChild: [dossierAccessGuard],
        loadComponent: () =>
          import('./routes/dossier/dossier.component').then(
            (c) => c.DossierComponent,
          ),
        resolve: { dossier: dossierResolver },
        data: { breadcrumb: PageTitle.ACCUEIL },
        children: [
          {
            path: 'accueil',
            canMatch: [() => inject(PreferenceService).isCollab],
            loadComponent: () =>
              import('./routes/dossier/dashboard-collab/dashboard-collab.component').then(
                (c) => c.DashboardCollabComponent,
              ),
            resolve: {
              helpCodeEXC: centralisateurHelpCodeResolver,
            },
          },
          {
            path: 'accueil',
            canMatch: [() => inject(PreferenceService).isClient],
            loadComponent: () =>
              import('./routes/dossier/dashboard-client/dashboard-client.component').then(
                (c) => c.DashboardClientComponent,
              ),
            data: {
              helpCodeWCL: 'WCL.010.010',
            },
          },
          {
            path: 'fiche-societe',
            data: { breadcrumb: PageTitle.FICHE_SOCIETE },
            children: [
              {
                path: 'dossier-permanent',
                loadComponent: () =>
                  import('./routes/dossier/dossier-permanent/dossier-permanent.component').then(
                    (c) => c.DossierPermanentComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.DOSSIER_PERMANENT,
                  droit: DroitClientFinal._DP,
                  helpCodeWCL: 'WCL.090.010',
                  helpCodeEXC: 'W.080.010',
                },
              },
              {
                path: 'edition-archive',
                loadComponent: () =>
                  import('./routes/dossier/edition-archive/edition-archive.component').then(
                    (c) => c.EditionArchiveComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.EDITION_ARCHIVE,
                  droit: DroitClientFinal._EDITIONS_ARCHIVEES,
                  helpCodeWCL: 'WCL.090.020',
                  helpCodeEXC: 'W.080.050',
                },
              },
              {
                path: 'coordonnees',
                canDeactivate: [pendingChangesGuard],
                loadComponent: () =>
                  import('./routes/dossier/fiche-societe-coordonnees/fiche-societe-coordonnees.component').then(
                    (c) => c.FicheSocieteCoordonneesComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.COORDONNEES,
                  helpCodeEXC: 'W.080.020',
                },
              },
              {
                path: 'banques',
                canDeactivate: [pendingChangesGuard],
                loadComponent: () =>
                  import('./routes/dossier/fiche-societe-banques/fiche-societe-banques.component').then(
                    (c) => c.FicheSocieteBanquesComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.BANQUES,
                  helpCodeEXC: 'W.080.030',
                },
              },
              {
                path: 'info-fiscale',
                canDeactivate: [pendingChangesGuard],
                loadComponent: () =>
                  import('./routes/dossier/fiche-societe-informations-fiscales/fiche-societe-informations-fiscales.component').then(
                    (c) => c.FicheSocieteInformationsFiscalesComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.INFO_FISCALES,
                  helpCodeEXC: 'W.080.040',
                },
              },
            ],
          },
          {
            path: 'comptabilite-generale',
            data: { breadcrumb: PageTitle.COMPTABILITE_GENERALE },
            children: [
              {
                path: 'saisie-standard',
                canDeactivate: [pendingChangesGuard],
                loadComponent: () =>
                  import('./routes/dossier/saisie-standard/saisie-standard.component').then(
                    (c) => c.SaisieStandardComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.SAISIE_STANDARD,
                  helpCodeWCL: 'WCL.020.010',
                  helpCodeEXC: 'W.020.010',
                  droit: DroitClientFinal._SAISIE,
                },
              },
              {
                path: 'revision',
                loadComponent: () =>
                  import('./routes/dossier/revision/revision.component').then(
                    (c) => c.RevisionComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.REVISION,
                  helpCodeWCL: 'WCL.020.020',
                  helpCodeEXC: 'W.020.020',
                },
              },
              {
                path: 'balances/exploitation',
                loadComponent: () =>
                  import('./routes/dossier/balances/balance-exploitation/balance-exploitation.component').then(
                    (c) => c.BalanceExploitationComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.BALANCE_EXPLOITATION,
                  helpCodeWCL: 'WCL.020.060',
                  helpCodeEXC: 'W.020.060',
                },
              },
              {
                path: 'balances/:type',
                loadComponent: () =>
                  import('./routes/dossier/balances/balances.component').then(
                    (c) => c.BalancesComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.BALANCE_GENERALE,
                },
                resolve: {
                  helpCodeWCL: balanceHelpCodeResolver,
                  helpCodeEXC: balanceHelpCodeResolver,
                },
              },
              {
                path: 'grand-livre/:type',
                loadComponent: () =>
                  import('./routes/dossier/grand-livre/grand-livre.component').then(
                    (c) => c.GrandLivreComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.GRAND_LIVRE_GENERAL,
                },
                resolve: {
                  helpCodeWCL: grandLivreHelpCodeResolver,
                  helpCodeEXC: grandLivreHelpCodeResolver,
                },
              },
            ],
          },
          {
            path: 'declarations',
            loadComponent: () =>
              import('./routes/dossier/declarations/declarations.component').then(
                (c) => c.DeclarationsComponent,
              ),
            data: {
              breadcrumb: PageTitle.DECLARATION,
              droit: DroitClientFinal._DECLARATION,
              helpCodeWCL: 'WCL.080',
              helpCodeEXC: 'W.060',
            },
          },
          {
            canActivate: [hasExerciceComptableGuard],
            path: 'immobilisations',
            loadComponent: () =>
              import('./routes/dossier/immobilisations/immobilisations.component').then(
                (c) => c.ImmobilisationsComponent,
              ),
            data: {
              breadcrumb: PageTitle.IMMOBILISATIONS,
              droit: DroitClientFinal._IMMOBILISATION,
              helpCodeEXC: 'W.040.010',
              helpCodeWCL: 'WCL.040',
            },
          },
          {
            path: 'tresorerie',
            canMatch: [() => inject(PreferenceService).isCollab],
            data: {
              breadcrumb: PageTitle.TRESORERIE,
              droit: DroitClientFinal._TRESORERIE,
            },
            children: [
              {
                path: 'rapprochement-bancaires',
                loadComponent: () =>
                  import('./routes/dossier/rapprochement-bancaire/rapprochement-bancaire.component').then(
                    (c) => c.RapprochementBancaireComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.LISTE_RAPPROCHEMENT_BANCAIRE,
                  droit: DroitClientFinal._TRESORERIE,
                  helpCodeEXC: 'W.030.010',
                },
              },
              {
                path: 'emprunt',
                loadComponent: () =>
                  import('./routes/dossier/emprunts/emprunts.component').then(
                    (c) => c.EmpruntsComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.EMPRUNT,
                  droit: DroitClientFinal._TRESORERIE,
                  helpCodeEXC: 'W.030.020',
                },
              },
            ],
          },
          {
            path: 'transactions',
            loadComponent: () =>
              import('./routes/dossier/transactions/transactions.component').then(
                (c) => c.TransactionsComponent,
              ),
            data: {
              breadcrumb: PageTitle.TRANSACTIONS,
              helpCodeWCL: 'WCL.030.010',
              helpCodeEXC: 'W.010.020',
            },
          },
          // ROUTES COLLABS
          {
            canActivate: [collabGuard],
            path: 'recherche-ecriture',
            loadComponent: () =>
              import('./routes/dossier/search-ecriture/search-ecriture.component').then(
                (c) => c.SearchEcritureComponent,
              ),
            data: {
              breadcrumb: PageTitle.RECHERCHE_ECRITURE,
              helpCodeEXC: 'W.090.010',
            },
          },
          {
            canActivate: [collabGuard],
            path: 'gestion-ocr',
            loadComponent: () =>
              import('./routes/dossier/suivi-depots-ocr/suivi-depots-ocr.component').then(
                (c) => c.SuiviDepotsOcrComponent,
              ),
            data: {
              breadcrumb: PageTitle.SUIVI_DEPOTS,
              helpCodeEXC: 'W.050.010',
            },
          },
          {
            canActivate: [collabGuard],
            path: 'etats-financiers',
            loadComponent: () =>
              import('./routes/dossier/etats-financiers/etats-financiers.component').then(
                (c) => c.EtatsFinanciersComponent,
              ),
            data: {
              breadcrumb: PageTitle.ETATS_FINANCIERS,
              helpCodeEXC: 'W.070',
            },
          },
          {
            canActivate: [collabGuard, dossierDeTravailGuard],
            path: 'dossier-de-travail',
            loadComponent: () =>
              import('./routes/dossier/dossier-de-travail/dossier-de-travail.component').then(
                (c) => c.DossierDeTravailComponent,
              ),
            data: {
              breadcrumb: PageTitle.DOSSIER_ANNUEL,
              helpCodeEXC: '010.060.010',
            },
          },
          // ROUTES CLIENTS
          {
            canActivate: [clientGuard],
            path: 'clients',
            loadComponent: () =>
              import('./routes/dossier/clients/clients.component').then(
                (c) => c.ClientsComponent,
              ),
            data: {
              breadcrumb: PageTitle.CLIENTS,
              droit: DroitClientFinal._CLIENT,
              helpCodeWCL: 'WCL.060',
            },
          },
          {
            canActivate: [clientGuard],
            path: 'fournisseurs',
            loadComponent: () =>
              import('./routes/dossier/fournisseurs/fournisseurs.component').then(
                (c) => c.FournisseursComponent,
              ),
            data: {
              breadcrumb: PageTitle.FOURNISSEURS,
              droit: DroitClientFinal._FOURNISSEUR,
              helpCodeWCL: 'WCL.070',
            },
          },
          {
            canActivate: [clientGuard],
            path: 'facture-a-payer',
            loadComponent: () =>
              import('./routes/dossier/factures-a-payer/factures-a-payer.component').then(
                (c) => c.FacturesAPayerComponent,
              ),
            data: {
              breadcrumb: PageTitle.FACTURE_A_PAYER,
              helpCodeWCL: 'WCL.010.020',
            },
          },
          {
            canActivate: [clientGuard],
            path: 'facture-electroniques',
            loadComponent: () =>
              import('./routes/dossier/facture-electroniques/facture-electroniques.component').then(
                (c) => c.FactureElectroniquesComponent,
              ),
            data: {
              breadcrumb: PageTitle.FACTURE_ELECTRONIQUES,
            },
          },
          {
            canActivate: [clientGuard],
            path: 'balances/client',
            loadComponent: () =>
              import('./routes/dossier/balances/balances.component').then(
                (c) => c.BalancesComponent,
              ),
            data: {
              breadcrumb: PageTitle.BALANCE_CLIENT,
              droit: DroitClientFinal._EDITION,
              helpCodeWCL: 'WCL.020.040',
            },
          },
          {
            canActivate: [clientGuard],
            path: 'balances/fournisseur',
            loadComponent: () =>
              import('./routes/dossier/balances/balances.component').then(
                (c) => c.BalancesComponent,
              ),
            data: {
              breadcrumb: PageTitle.BALANCE_FOURNISSEUR,
              droit: DroitClientFinal._EDITION,
              helpCodeWCL: 'WCL.020.050',
            },
          },
          {
            canActivate: [clientGuard],
            path: 'piece-comptables',
            loadComponent: () =>
              import('./routes/dossier/piece-comptables/piece-comptables.component').then(
                (c) => c.PieceComptablesComponent,
              ),
            data: {
              breadcrumb: PageTitle.PIECE_COMPTABLES,
            },
          },
          {
            path: 'facturation',
            children: [
              {
                canActivate: [clientGuard],
                path: 'emetteur',
                canDeactivate: [pendingChangesGuard],
                loadComponent: () =>
                  import('./routes/dossier/entite-facturation/entite-facturation.component').then(
                    (c) => c.EntiteFacturationComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.EMETEUR,
                  helpCodeWCL: 'WCL.100.010',
                },
              },
              {
                path: 'article-facturations',
                loadComponent: () =>
                  import('./routes/gestion-interne/article-facturations/article-facturations.component').then(
                    (c) => c.ArticleFacturationsComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.ARTICLES_FACTURATION,
                  helpCodeWCL: 'WCL.100.030',
                },
              },
              {
                path: 'factures',
                loadComponent: () =>
                  import('./routes/gestion-interne/factures/factures.component').then(
                    (c) => c.FacturesComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.FACTURES,
                  helpCodeWCL: 'WCL.100.040',
                },
              },
              {
                path: 'devis',
                loadComponent: () =>
                  import('./routes/gestion-interne/devis/devis.component').then(
                    (c) => c.DevisComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.DEVIS,
                  helpCodeWCL: 'WCL.100.050',
                },
              },
              {
                canActivate: [clientGuard],
                path: 'clients',
                loadComponent: () =>
                  import('./routes/gestion-interne/client-facturations/client-facturations.component').then(
                    (c) => c.ClientFacturationsComponent,
                  ),
                data: {
                  breadcrumb: PageTitle.CLIENT_FACTURATIONS,
                  helpCodeWCL: 'WCL.100.020',
                },
              },
            ],
          },
          { path: '', redirectTo: 'accueil', pathMatch: 'full' },
          { path: '**', redirectTo: 'accueil' },
        ],
      },
    ],
  },
  {
    path: 'not-found',
    title: 'Logaia',
    loadComponent: () =>
      import('@shared/components/empty/empty.component').then(
        (c) => c.EmptyComponent,
      ),
  },
  { path: '**', redirectTo: 'not-found' },
];
